import { useCallback, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { TOP_PROGRESS } from '@/components/layout/top-progress-store';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useAuth } from '@/hooks/auth/useAuth';
import type { Snapshot } from './useLiveAgentTurnSignals';
import { unseenActivityFor } from './agent-activity-utils';
import type { Agent, AgentActivity } from '@/lib/types';

/** Per-agent last-viewed timestamps for the signed-in user: agentId -> epoch-ms. */
export type AgentViewsMap = Readonly<Record<string, number>>;

const EMPTY: AgentViewsMap = Object.freeze({});
// This is deliberately not a clock value. While a view POST is pending, it
// makes every real epoch-millisecond activity timestamp compare as seen. The
// successful response always replaces it with the server-owned timestamp.
const OPTIMISTIC_VIEWED_AT = Number.MAX_SAFE_INTEGER;
/**
 * The `lastViewedAt` an explicitly-unread view carries (mirrors the server's
 * `UNREAD_VIEWED_AT`): the epoch, so every real activity timestamp compares as
 * unseen. Kept as a real row rather than a missing one — a missing row means
 * "never opened", which reads as seen.
 */
export const UNREAD_VIEWED_AT = 0;

/** The activity timestamp we compare against — `lastActiveAt`, or the creation
 * time for legacy runs that never recorded one (matching `agentSortTimestamp`). */
function agentActiveAt(agent: Agent): number {
  return agent.lastActiveAt ?? agent.createdAt;
}

/**
 * True when the agent has a finished-turn change the viewer has not looked at
 * yet. Activity timestamps also advance for in-flight tool calls, so suppress
 * the cue while a turn is still working; the turn-end activity stamp makes the
 * cue appear once the response is ready. Never true for an agent the viewer
 * has never opened: "new changes since you last looked" only means something
 * once you've looked at least once, so an agent with no view row is treated as
 * seen (no baseline noise).
 *
 * `activity` must be the evidence-gated value from `unseenActivityFor`, not
 * the display default: a `running` agent with no turn-boundary evidence has
 * to count as working here, or the cue would show without proof a turn ended.
 */
export function isAgentUnseen(
  views: AgentViewsMap,
  agent: Agent,
  activity: AgentActivity,
): boolean {
  if (activity === 'starting' || activity === 'working') return false;
  const viewedAt = views[agent.id];
  return viewedAt !== undefined && agentActiveAt(agent) > viewedAt;
}

/**
 * Whether a run is unread for the signed-in viewer, derived from the same two
 * inputs the sidebar row uses for its unseen dot: the per-viewer views map and
 * the shared live turn-signal snapshot. This composes {@link isAgentUnseen}
 * with {@link unseenActivityFor} — the evidence-gated activity the row dot
 * also uses — so the "unread" filter and the dot can never disagree about
 * what counts as unread.
 *
 * Returns false when either input is absent, so callers that don't thread in
 * live read state get an empty unread set rather than silently treating every
 * run as unread.
 */
export function isAgentUnread(
  views: AgentViewsMap | undefined,
  activities: Snapshot | undefined,
  agent: Agent,
): boolean {
  if (!views || !activities) return false;
  return isAgentUnseen(views, agent, unseenActivityFor(agent, activities));
}

/**
 * The signed-in user's per-agent "last looked at" map, from `GET /agents/views`.
 * Server-backed so it syncs across devices; the sidebar joins it with each
 * agent's live `lastActiveAt` to derive the unseen dot. Deduped by React Query,
 * so every row can call this and share one request.
 */
export function useAgentViews(): AgentViewsMap {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const viewerId = useAuth().user?.id ?? null;
  const key = useMemo(() => [...queryKeys.agentViews(orgKey, viewerId)], [orgKey, viewerId]);
  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await api.listAgentViews();
      const map: Record<string, number> = {};
      for (const v of res.views) map[v.agentId] = v.lastViewedAt;
      // A view POST can update this cache while the list request is in flight.
      // Persisted timestamps only advance and the optimistic sentinel must win,
      // so merge instead of letting an older GET snapshot erase either one.
      // An explicit "mark unread" is the one value that isn't a timestamp to
      // be maxed: it deliberately rewinds, so a snapshot taken before it must
      // not restore the old timestamp.
      const current = queryClient.getQueryData<AgentViewsMap>(key) ?? EMPTY;
      for (const [agentId, viewedAt] of Object.entries(current)) {
        map[agentId] =
          viewedAt === UNREAD_VIEWED_AT ? UNREAD_VIEWED_AT : Math.max(map[agentId] ?? 0, viewedAt);
      }
      return map;
    },
    // Views change only when this user opens an agent (and we patch the cache
    // optimistically then), so a short stale window avoids needless refetches.
    staleTime: 30_000,
  });
  return data ?? EMPTY;
}

/**
 * Why a view is being recorded. `open` is the user deliberately looking at the
 * agent; `leave` is the trailing write when it stops being the open one. Only
 * the reason distinguishes the two writes, and marking an agent unread while
 * it is open must survive the `leave` write — otherwise navigating away would
 * silently undo the action the user just took.
 */
export type RecordAgentViewReason = 'open' | 'leave';

export interface UseRecordAgentView {
  /** Record that the current user is looking at this agent right now. */
  record: (agentId: string, reason: RecordAgentViewReason) => void;
}

interface PendingAgentViews {
  latestRequestId: number;
  pendingRequestIds: Set<number>;
  latestServerViewedAt?: number;
}

interface RecordAgentViewRequest {
  agentId: string;
  requestId: number;
  queryKey: QueryKey;
}

/**
 * Record a view of an agent (clears its unseen dot). Optimistically advances the
 * local views map so the dot clears instantly, then reconciles with the exact
 * server timestamp; a failed write re-syncs from the server on the next fetch.
 */
export function useRecordAgentView(): UseRecordAgentView {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const viewerId = useAuth().user?.id ?? null;
  const key = useMemo(() => [...queryKeys.agentViews(orgKey, viewerId)], [orgKey, viewerId]);
  // A view is recorded when an agent opens and again when it closes. Track
  // overlapping requests per agent so an old response cannot rewind a newer
  // server value and an older success can still recover from a newer failure.
  const nextRequestIdRef = useRef(0);
  const pendingByAgentRef = useRef(new Map<string, PendingAgentViews>());

  const setOptimisticViewedAt = useCallback(
    (queryKey: QueryKey, agentId: string) => {
      queryClient.setQueryData<Record<string, number>>(queryKey, (old) => {
        if (old?.[agentId] === OPTIMISTIC_VIEWED_AT) return old;
        return { ...(old ?? {}), [agentId]: OPTIMISTIC_VIEWED_AT };
      });
    },
    [queryClient],
  );

  const setAuthoritativeViewedAt = useCallback(
    (queryKey: QueryKey, agentId: string, at: number) => {
      queryClient.setQueryData<Record<string, number>>(queryKey, (old) => ({
        ...(old ?? {}),
        [agentId]: at,
      }));
    },
    [queryClient],
  );

  const clearViewedAt = useCallback(
    (queryKey: QueryKey, agentId: string) => {
      queryClient.setQueryData<Record<string, number>>(queryKey, (old) => {
        if (old?.[agentId] === undefined) return old ?? {};
        const next = { ...old };
        delete next[agentId];
        return next;
      });
    },
    [queryClient],
  );

  const { mutate } = useMutation({
    // Recording a view is a silent background write; keep the top progress bar idle.
    meta: { [TOP_PROGRESS]: false },
    mutationFn: ({ agentId }: RecordAgentViewRequest) => api.recordAgentView(agentId),
    onSuccess: (view, { agentId, requestId, queryKey }) => {
      const pending = pendingByAgentRef.current.get(agentId);
      if (!pending?.pendingRequestIds.delete(requestId)) return;
      pending.latestServerViewedAt = Math.max(pending.latestServerViewedAt ?? 0, view.lastViewedAt);

      // Keep the latest optimistic value while that request is still pending.
      // Once it settles, any successful response is safe to apply: all server
      // timestamps share a clock and the endpoint only moves them forward.
      if (pending.pendingRequestIds.has(pending.latestRequestId)) return;

      // The server owns both `lastViewedAt` and the agent's `lastActiveAt`.
      // Always replace the optimistic sentinel with its response so the
      // eventual unseen comparison uses two values from the same clock.
      setAuthoritativeViewedAt(queryKey, agentId, pending.latestServerViewedAt);
      if (pending.pendingRequestIds.size === 0) pendingByAgentRef.current.delete(agentId);
    },
    onError: (_error, { agentId, requestId, queryKey }) => {
      const pending = pendingByAgentRef.current.get(agentId);
      if (!pending?.pendingRequestIds.delete(requestId)) return;
      if (pending.pendingRequestIds.has(pending.latestRequestId)) return;

      if (pending.latestServerViewedAt !== undefined) {
        setAuthoritativeViewedAt(queryKey, agentId, pending.latestServerViewedAt);
      } else if (pending.pendingRequestIds.size > 0) {
        // An older request can still establish the baseline. Leave the
        // optimistic value in place until it settles.
        return;
      } else {
        clearViewedAt(queryKey, agentId);
      }

      if (pending.pendingRequestIds.size === 0) {
        pendingByAgentRef.current.delete(agentId);
        void queryClient.invalidateQueries({ queryKey, exact: true });
      }
    },
  });

  // `mutate` is referentially stable across renders (React Query guarantee), so
  // `record` stays stable — the open/leave effect in AgentsPage must not re-run
  // (and re-POST) on every render.
  const record = useCallback(
    (agentId: string, reason: RecordAgentViewReason) => {
      const cached = queryClient.getQueryData<AgentViewsMap>(key)?.[agentId];
      // Marked unread while it was open: hold that state until the user opens
      // the agent again, instead of letting the trailing leave write clear it.
      if (reason === 'leave' && cached === UNREAD_VIEWED_AT) return;
      const requestId = ++nextRequestIdRef.current;
      const cachedViewedAt = cached ?? OPTIMISTIC_VIEWED_AT;
      const pending = pendingByAgentRef.current.get(agentId) ?? {
        latestRequestId: requestId,
        pendingRequestIds: new Set<number>(),
        ...(cachedViewedAt === OPTIMISTIC_VIEWED_AT
          ? {}
          : { latestServerViewedAt: cachedViewedAt }),
      };
      pending.latestRequestId = requestId;
      pending.pendingRequestIds.add(requestId);
      pendingByAgentRef.current.set(agentId, pending);
      // Optimistic clear now; the latest mutation response replaces the
      // non-clock sentinel with the authoritative server timestamp.
      setOptimisticViewedAt(key, agentId);
      mutate({ agentId, requestId, queryKey: key });
    },
    [queryClient, key, setOptimisticViewedAt, mutate],
  );

  return { record };
}

/**
 * Mark an agent unread for the signed-in user — the inverse of
 * {@link useRecordAgentView}. The row goes back to bold with its unseen dot,
 * and stays that way until the user opens the agent again.
 *
 * The views map is patched immediately so the row changes under the cursor,
 * then reconciled with the server's own value. A failed write reverts the row
 * to the timestamp it had, so a silent failure can't leave a lie on screen.
 */
export function useMarkAgentUnread() {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const viewerId = useAuth().user?.id ?? null;
  const key = useMemo(() => [...queryKeys.agentViews(orgKey, viewerId)], [orgKey, viewerId]);

  return useMutation({
    mutationFn: (agentId: string) => api.markAgentUnread(agentId),
    onMutate: (agentId) => {
      const previous = queryClient.getQueryData<AgentViewsMap>(key)?.[agentId];
      queryClient.setQueryData<Record<string, number>>(key, (old) => ({
        ...(old ?? {}),
        [agentId]: UNREAD_VIEWED_AT,
      }));
      return { previous };
    },
    onSuccess: (view, agentId) => {
      queryClient.setQueryData<Record<string, number>>(key, (old) => ({
        ...(old ?? {}),
        [agentId]: view.lastViewedAt,
      }));
    },
    onError: (_error, agentId, context) => {
      queryClient.setQueryData<Record<string, number>>(key, (old) => {
        const next = { ...(old ?? {}) };
        if (context?.previous === undefined) delete next[agentId];
        else next[agentId] = context.previous;
        return next;
      });
    },
  });
}
